export { AttentionMachine, DEFAULT_MODEL, DEFAULT_THRESHOLDS, DEFAULT_WASM_BASE, Emitter, EyeContact, FaceDetector, Logger, eulerFromMatrix, evaluateFrame, gazeFromBlendshapes } from './chunk-7QXGZ2S3.js';

// src/controllers/html-media.ts
function htmlMediaController(el, options = {}) {
  const respectUserPause = options.respectUserPause ?? true;
  const pendingTimeoutMs = options.pendingTimeoutMs ?? 5e3;
  let pendingPauseAt = null;
  let userPaused = false;
  const consumePendingPause = () => {
    if (pendingPauseAt === null) return false;
    const fresh = Date.now() - pendingPauseAt <= pendingTimeoutMs;
    pendingPauseAt = null;
    return fresh;
  };
  const onPause = () => {
    if (!consumePendingPause()) userPaused = true;
  };
  const onPlay = () => {
    pendingPauseAt = null;
    userPaused = false;
  };
  el.addEventListener("pause", onPause);
  el.addEventListener("play", onPlay);
  return {
    name: `html-media(${el.tagName.toLowerCase()})`,
    pause() {
      if (el.paused) return;
      pendingPauseAt = Date.now();
      el.pause();
    },
    play() {
      if (!el.paused) return;
      if (el.ended) return;
      pendingPauseAt = null;
      const p = el.play();
      if (p && typeof p.then === "function") {
        return p.catch((err) => {
          throw err instanceof Error ? err : new Error(String(err));
        });
      }
    },
    isPausedByUser: () => respectUserPause && userPaused,
    destroy() {
      el.removeEventListener("pause", onPause);
      el.removeEventListener("play", onPlay);
    }
  };
}

// src/controllers/youtube.ts
var YT_STATE = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5
};
function youtubeController(player, options = {}) {
  const respectUserPause = options.respectUserPause ?? true;
  const ignoreEnded = options.ignoreEnded ?? true;
  const pendingTimeoutMs = options.pendingTimeoutMs ?? 5e3;
  let pendingPauseAt = null;
  let userPaused = false;
  const consumePendingPause = () => {
    if (pendingPauseAt === null) return false;
    const fresh = Date.now() - pendingPauseAt <= pendingTimeoutMs;
    pendingPauseAt = null;
    return fresh;
  };
  const onStateChange = (e) => {
    const state = e?.data;
    if (state === YT_STATE.PAUSED) {
      if (!consumePendingPause()) userPaused = true;
    } else if (state === YT_STATE.PLAYING) {
      pendingPauseAt = null;
      userPaused = false;
    }
  };
  player.addEventListener?.("onStateChange", onStateChange);
  return {
    name: "youtube",
    pause() {
      const state = player.getPlayerState();
      if (state !== YT_STATE.PLAYING && state !== YT_STATE.BUFFERING) return;
      pendingPauseAt = Date.now();
      player.pauseVideo();
    },
    play() {
      const state = player.getPlayerState();
      if (state === YT_STATE.PLAYING || state === YT_STATE.BUFFERING) return;
      if (ignoreEnded && state === YT_STATE.ENDED) return;
      if (state === YT_STATE.UNSTARTED || state === YT_STATE.CUED) return;
      pendingPauseAt = null;
      player.playVideo();
    },
    isPausedByUser: () => respectUserPause && userPaused,
    destroy() {
      player.removeEventListener?.("onStateChange", onStateChange);
    }
  };
}
var apiPromise = null;
function loadYouTubeIframeApi() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("loadYouTubeIframeApi requires a browser"));
  }
  const w = window;
  if (w.YT?.Player) return Promise.resolve(w.YT);
  if (apiPromise) return apiPromise;
  const previous = w.onYouTubeIframeAPIReady;
  const attempt = new Promise((resolve, reject) => {
    let settled = false;
    w.onYouTubeIframeAPIReady = () => {
      settled = true;
      previous?.();
      resolve(w.YT);
    };
    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    script.onerror = () => reject(new Error("Failed to load the YouTube IFrame API"));
    document.head.appendChild(script);
    setTimeout(() => {
      if (!settled) reject(new Error("YouTube IFrame API timed out after 15s"));
    }, 15e3);
  });
  apiPromise = attempt;
  attempt.catch(() => {
    if (apiPromise === attempt) apiPromise = null;
    w.onYouTubeIframeAPIReady = previous;
  });
  return attempt;
}
function parseYouTubeId(input) {
  const value = input.trim();
  if (!value) return null;
  if (/^[\w-]{11}$/.test(value)) return value;
  try {
    const url = new URL(value.startsWith("http") ? value : `https://${value}`);
    const host = url.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = url.pathname.slice(1).split("/")[0];
      return id && /^[\w-]{11}$/.test(id) ? id : null;
    }
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      const v = url.searchParams.get("v");
      if (v && /^[\w-]{11}$/.test(v)) return v;
      const match = url.pathname.match(/\/(embed|shorts|live|v)\/([\w-]{11})/);
      if (match?.[2]) return match[2];
    }
  } catch {
    return null;
  }
  return null;
}

// src/controllers/custom.ts
function customController(options) {
  const controller = {
    name: options.name ?? "custom",
    pause: options.onPause,
    play: options.onPlay
  };
  if (options.isPausedByUser) controller.isPausedByUser = options.isPausedByUser;
  if (options.onDestroy) controller.destroy = options.onDestroy;
  return controller;
}

export { YT_STATE, customController, htmlMediaController, loadYouTubeIframeApi, parseYouTubeId, youtubeController };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map